import type { FactionId } from "@/data/factions.ts";
import type { RoleId } from "@/data/roles.ts";

/**
 * Who you are, stored on this device.
 *
 * The player id is the important part: it is generated once and kept forever.
 * Reconnecting after a crash restores your seat in the roster and, critically,
 * your command if you held it. Without a stable id a commander who alt-tabs
 * into a crash comes back as a stranger and the team is leaderless.
 *
 * None of this is an account. There is no server to register with and nothing
 * here identifies a person — it is a random id and a callsign you chose.
 */

const STORAGE_KEY = "wardogs-identity";

export interface Identity {
  readonly id: string;
  readonly callsign: string;
  readonly faction: FactionId;
  readonly role: RoleId;
}

export interface StoredIdentity {
  readonly id: string;
  callsign: string;
  faction: FactionId;
  role: RoleId;
}

function createId(): string {
  return crypto.randomUUID();
}

/** Read the stored identity, creating one on first run. */
export function loadIdentity(): StoredIdentity {
  const fallback: StoredIdentity = {
    id: createId(),
    callsign: "",
    faction: "lonestar",
    role: "assault",
  };

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return fallback;

    const record = parsed as Record<string, unknown>;
    return {
      id: typeof record["id"] === "string" && record["id"] ? record["id"] : fallback.id,
      callsign: typeof record["callsign"] === "string" ? record["callsign"] : "",
      faction: (record["faction"] as FactionId) ?? fallback.faction,
      role: (record["role"] as RoleId) ?? fallback.role,
    };
  } catch {
    // A corrupt or unavailable store must never stop someone joining.
    return fallback;
  }
}

export function saveIdentity(identity: StoredIdentity): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Private browsing and full quotas are survivable: the session still works,
    // it just will not be remembered next time.
  }
}

/**
 * Suggest a callsign so nobody has to invent one under time pressure.
 * Phonetic alphabet plus a number, which is also what people say on comms.
 */
const PHONETIC = [
  "ALPHA",
  "BRAVO",
  "CHARLIE",
  "DELTA",
  "ECHO",
  "FOXTROT",
  "GOLF",
  "HOTEL",
  "INDIA",
  "JULIET",
  "KILO",
  "LIMA",
  "MIKE",
  "NOVEMBER",
  "OSCAR",
  "PAPA",
  "ROMEO",
  "SIERRA",
  "TANGO",
  "VICTOR",
  "WHISKEY",
  "XRAY",
  "YANKEE",
  "ZULU",
] as const;

export function suggestCallsign(): string {
  const index = Math.floor(Math.random() * PHONETIC.length);
  const word = PHONETIC[index] ?? "ALPHA";
  return `${word}-${Math.floor(Math.random() * 9) + 1}`;
}

/** Trim and cap a typed callsign. Empty input falls back to a suggestion. */
export function normalizeCallsign(input: string): string {
  const trimmed = input.trim().replace(/\s+/g, " ").slice(0, 20);
  return trimmed.length > 0 ? trimmed : suggestCallsign();
}
